import { useEffect, useState } from 'react'

import { PatientSourceEnum } from '@/core/enums/patient-source.enum'
import { useSearchPatientsQuery } from '@/core/services/patient'
import { useIsUS } from '@/hooks/useIsUS'
import { formatBirthday } from '@/utils/dateUtils'

import { looksLikeQuestion } from '../lib/omnibox-actions'
import { OmniboxPatient, OmniboxPatientSearch } from '../lib/omnibox-options'

const MIN_QUERY_LENGTH = 2
const MAX_RESULTS = 5

/**
 * Debounced patient lookup for the omnibox. Stays idle for short queries, for sentences
 * addressed to ELARA, and when the user cannot see the patient directory.
 */
export function useOmniboxPatients(query: string, enabled: boolean): OmniboxPatientSearch {
  const isUS = useIsUS()
  const liveQuery = query.trim()
  const [debouncedQuery, setDebouncedQuery] = useState(liveQuery)
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(liveQuery), 300)
    return () => window.clearTimeout(timer)
  }, [liveQuery])

  const isSearchable: boolean = enabled && liveQuery.length >= MIN_QUERY_LENGTH && !looksLikeQuestion(liveQuery)
  const isDebouncing: boolean = debouncedQuery !== liveQuery
  const skip: boolean = !isSearchable || isDebouncing

  const { patients, isFetching } = useSearchPatientsQuery(
    {
      patientSource: PatientSourceEnum.MedconnectPatient,
      smartSearch: debouncedQuery,
      limit: MAX_RESULTS,
      skip: 0,
      orderBy: 'userInfo.surname',
    },
    {
      skip,
      selectFromResult: ({ currentData, isFetching: fetching }) => ({
        isFetching: fetching,
        patients: (currentData?.items ?? []).map(
          (patient): OmniboxPatient => ({
            id: patient._id,
            fullName: [patient.userInfo.surname?.toUpperCase(), patient.userInfo.name].filter(Boolean).join(' '),
            birthday: patient.userInfo.birthday ? formatBirthday(patient.userInfo.birthday, isUS) : undefined,
          })
        ),
      }),
    }
  )

  if (!isSearchable) return { patients: [], isSearching: false }
  return { patients: isDebouncing ? [] : patients, isSearching: isDebouncing || isFetching }
}
